// F-536 (URS_12.3) — one 30/60/90-day questionnaire, answered in the app.
//
// Renders the instrument the kernel issued, verbatim: the unnumbered opening item ("Who is
// completing this survey?"), then each numbered question with its answer choices. Single-choice
// questions behave as radios, "(Select all that apply)" as checkboxes. A count ("if yes, how
// many times?") and a comment ("Other:", "Yes: … (optional short comment)") appear only under the
// option that asks for them and are never required. Submit is enabled once every question has
// an answer.
//
// ⛔ The kernel decides which answers need staff attention; nothing here marks, hints at or
// hides a "flagged" option — the patient answers the questions as written.

import React, { useState } from "react";
import { IonButton, IonInput, IonTextarea } from "@ionic/react";

import {
  Draft,
  Instrument,
  InstrumentQuestion,
  choose,
  countIsValid,
  countOption,
  emptyDraft,
  isComplete,
  textOption,
  toAnswers,
} from "./surveyInstrument";

interface Props {
  instrument: Instrument;
  busy: boolean;
  onSubmit: (answers: Record<string, unknown>) => void;
  onCancel: () => void;
}

const QuestionBlock: React.FC<{
  question: InstrumentQuestion;
  draft: Draft;
  setDraft: (next: Draft) => void;
  disabled: boolean;
}> = ({ question, draft, setDraft, disabled }) => {
  const answer = draft[question.id];
  const multi = question.type === "multi_choice";
  const counted = countOption(question, answer);
  const commented = textOption(question, answer);
  const promptId = `survey-q-${question.id}`;

  return (
    <fieldset className="survey-question" data-testid={`survey-question-${question.id}`}>
      <legend id={promptId} className="survey-question-prompt">
        {question.number !== null ? `${question.number}. ` : ""}
        {question.prompt}
      </legend>
      <div role={multi ? "group" : "radiogroup"} aria-labelledby={promptId}>
        {question.options.map((option) => {
          const selected = !!answer?.codes.includes(option.code);
          return (
            <button
              key={option.code}
              type="button"
              role={multi ? "checkbox" : "radio"}
              aria-checked={selected}
              className={`survey-option${selected ? " is-selected" : ""}`}
              disabled={disabled}
              onClick={() => setDraft(choose(draft, question, option.code))}
            >
              <span className={`survey-option-mark${multi ? " is-multi" : ""}`} aria-hidden />
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
      {counted?.count && (
        <label className="survey-followup">
          <span>{counted.count.prompt}</span>
          <IonInput
            type="number"
            inputmode="numeric"
            min={String(counted.count.min ?? 0)}
            max={counted.count.max !== undefined ? String(counted.count.max) : undefined}
            value={answer?.count ?? ""}
            aria-label={counted.count.prompt}
            disabled={disabled}
            onIonInput={(event) =>
              setDraft({
                ...draft,
                [question.id]: { ...(answer ?? emptyDraft()), count: String(event.detail.value ?? "") },
              })
            }
            className={countIsValid(counted, answer?.count ?? "") ? "" : "ion-invalid"}
          />
        </label>
      )}
      {commented?.free_text && (
        <IonTextarea
          className="survey-followup"
          value={answer?.text ?? ""}
          placeholder={commented.free_text.hint ? `(${commented.free_text.hint})` : undefined}
          aria-label={commented.free_text.hint ?? commented.label}
          autoGrow
          maxlength={1000}
          disabled={disabled}
          onIonInput={(event) =>
            setDraft({
              ...draft,
              [question.id]: { ...(answer ?? emptyDraft()), text: String(event.detail.value ?? "") },
            })
          }
        />
      )}
    </fieldset>
  );
};

const SurveyQuestionnaire: React.FC<Props> = ({ instrument, busy, onSubmit, onCancel }) => {
  const [draft, setDraft] = useState<Draft>({});
  const complete = isComplete(instrument, draft);

  return (
    <form
      className="survey-questionnaire"
      data-testid="survey-questionnaire"
      onSubmit={(event) => event.preventDefault()}
    >
      {instrument.questions.map((question) => (
        <QuestionBlock
          key={question.id}
          question={question}
          draft={draft}
          setDraft={setDraft}
          disabled={busy}
        />
      ))}
      <div className="engagement-survey-actions">
        <IonButton
          size="small"
          disabled={!complete || busy}
          onClick={() => {
            if (complete && !busy) onSubmit(toAnswers(instrument, draft));
          }}
        >
          Submit survey
        </IonButton>
        <IonButton size="small" fill="clear" disabled={busy} onClick={onCancel}>
          Cancel
        </IonButton>
      </div>
    </form>
  );
};

export default SurveyQuestionnaire;
